/**
 * Order Blocks (OB)
 *
 * Port of TradingView's open-source "Order Blocks" by MarkMiddleton2020
 * (final Pine v5 version 14, Apr 18 2024 — https://in.tradingview.com/script/GecN34Qq-Order-Blocks/).
 *
 * What it does (mirrors the TradingView script):
 * - Structure: the lowest low of the previous "Candle Range" bars
 *   (structureLow = ta.lowest(low, range)[1], structureLowIndex = index of
 *   the most recent bar holding that low).
 * - BEARISH break: `bearishBreakerSource` crosses under structureLow →
 *   a bearish order block is drawn from the most recent up candle
 *   (left = lastUpIndex, top = highest high since that candle, bottom =
 *   its low), extended right until price closes above its top.
 * - BULLISH break: `bullishBreakerSource` closes above an existing
 *   bearish OB's top → that box is deleted and a bullish order block is
 *   drawn from the most recent down candle (left = lastDownIndex, top =
 *   its high, bottom = lowest low since that candle), extended right
 *   until a close below its bottom.
 * - Mitigation / re-test: a bar piercing a live box marks it mitigated
 *   (state=1). With "Show Mitigated Blocks" on, mitigated boxes turn the
 *   grey mitigated colour (longs are marked even when the toggle is off —
 *   exactly as in Pine).
 * - Optional BOS lines: bounded horizontal segments from the structure
 *   point to the break bar (red, width 2) and from the removed bearish
 *   OB's left/top to the break bar (green, width 1). Capped at the last
 *   50 (Pine's default max_lines_count).
 * - Optional PDH/PDL: previous session high/low drawn as full-width
 *   blue lines with axis labels (TV draws them only on the last bar).
 * - Optional candle colours: trend (lime/red), breaker (yellow),
 *   re-test (orange/purple) — identical override order to Pine's
 *   barcolor() calls; delivered through metadata.candleColors and
 *   applied by ChartPanel's candle colour map.
 * - BUY/SELL ARROWS (addition requested for this app, not in the TV
 *   script): a green arrowUp below the bar that closes through a
 *   bearish OB / breaks structure up (bullish break) and a red
 *   arrowDown above the bar that breaks structure down or closes
 *   through a bullish OB's bottom (invalidation). One arrow per bar.
 *
 * Platform differences (documented):
 * - "Trigger Alerts" (TV's showAlerts) is omitted — this app has no
 *   Pine alertcondition equivalent for plain indicators.
 * - PDH/PDL group candles by UTC date (Pine uses the exchange session).
 * - OB fill alphas are the TV default input alphas (bear 20%, bull 40%,
 *   mitigated 20%); the settings colour picker selects the hue only.
 *
 * Category: Advanced | Pane: Overlay
 */
import {
  IndicatorDefinition, IndicatorParam, PlotData,
  IndicatorBand, IndicatorMarker, IndicatorPriceLine,
} from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

// ─── TV constants ────────────────────────────────────────────────────────────

/** Fill alphas from the TV input defaults (transparency 80/60/80). */
const ALPHA_BEAR = 0.2;
const ALPHA_BULL = 0.4;
const ALPHA_MITIGATED = 0.2;
/** box border color.rgb(207,203,202, 100) = fully transparent */
const BOX_BORDER = "rgba(207,203,202,0)";
const BOS_BEAR = "#FF0000";   // color.red
const BOS_BULL = "#008000";   // color.green
const PD_LINE = "#0000FF";    // color.blue
const MAX_BOS_LINES = 50;     // Pine default max_lines_count

/** Buy/sell arrow colours (app addition). */
const ARROW_UP_COLOR = "#00B450";
const ARROW_DOWN_COLOR = "#DC3232";

const rgba = (hex: string, alpha: number): string => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const SOURCE_OPTIONS = [
  { label: "Open", value: "open" },
  { label: "High", value: "high" },
  { label: "Low", value: "low" },
  { label: "Close", value: "close" },
  { label: "HL2", value: "hl2" },
  { label: "HLC3", value: "hlc3" },
  { label: "OHLC4", value: "ohlc4" },
];

const srcSeries = (c: CandleData[], key: string): number[] => {
  switch (key) {
    case "open": return c.map(x => x.open);
    case "high": return c.map(x => x.high);
    case "low": return c.map(x => x.low);
    case "hl2": return c.map(x => (x.high + x.low) / 2);
    case "hlc3": return c.map(x => (x.high + x.low + x.close) / 3);
    case "ohlc4": return c.map(x => (x.open + x.high + x.low + x.close) / 4);
    default: return c.map(x => x.close);
  }
};

// ─── Parameters (TradingView input groups) ───────────────────────────────────

const OB_PARAMS: IndicatorParam[] = [
  // BASIC SETTINGS
  { key: "candleRange", label: "Candle Range", type: "number", default: 25, min: 5, max: 100, step: 1, group: "BASIC SETTINGS" },
  { key: "bullBreakSource", label: "Bullish Breaker Source", type: "select", default: "close", options: SOURCE_OPTIONS, group: "BASIC SETTINGS" },
  { key: "bearBreakSource", label: "Bearish Breaker Source", type: "select", default: "close", options: SOURCE_OPTIONS, group: "BASIC SETTINGS" },
  // Extras
  { key: "showPD", label: "Show Previous Day High/Low", type: "boolean", default: false, group: "Extras" },
  { key: "showBearishBOS", label: "Show Bearish BOS Line", type: "boolean", default: false, group: "Extras" },
  { key: "showBullishBOS", label: "Show Bullish BOS Line", type: "boolean", default: false, group: "Extras" },
  { key: "showBreakerCandles", label: "Highlight Breaker Candles", type: "boolean", default: false, group: "Extras" },
  { key: "showRetestCandles", label: "Highlight Re-test Candles", type: "boolean", default: false, group: "Extras" },
  { key: "showTrendColours", label: "Show Trend Colours", type: "boolean", default: false, group: "Extras" },
  { key: "useMitigatedBlocks", label: "Show Mitigated Blocks", type: "boolean", default: false, group: "Extras" },
  { key: "showArrows", label: "Show Buy/Sell Arrows", type: "boolean", default: true, group: "Extras" },
  // STYLES
  { key: "bearishOBColor", label: "Bearish Order Block Colour", type: "color", default: "#DBA632", group: "STYLES" },
  { key: "bullishOBColor", label: "Bullish Order Block Colour", type: "color", default: "#C0E6AE", group: "STYLES" },
  { key: "mitigatedOBColor", label: "Mitigated Order Block Colour", type: "color", default: "#CFCBCA", group: "STYLES" },
  { key: "breakerCandleColor", label: "Breaker Candle Colour", type: "color", default: "#FFFF00", group: "STYLES" },
  { key: "shortRetestColor", label: "Short Re-Test Candle Colour", type: "color", default: "#800080", group: "STYLES" },
  { key: "longRetestColor", label: "Long Re-Test Candle Colour", type: "color", default: "#FFA500", group: "STYLES" },
  { key: "trendBullColor", label: "Bullish Trend Colour", type: "color", default: "#00FF00", group: "STYLES" },
  { key: "trendBearColor", label: "Bearish Trend Colour", type: "color", default: "#FF0000", group: "STYLES" },
];

// ─── Live order-block box (Pine box + parallel arrays) ───────────────────────

interface ObBox {
  left: number;    // bar index of the OB origin candle (box.new left)
  top: number;
  bottom: number;
  start: number;   // first bar after creation eligible for re-test (Pine longBoxStart/shortBoxStart)
  state: number;   // 0 = untouched, 1 = mitigated
}

interface BosLine {
  left: number;
  right: number;
  price: number;
  color: string;
  width: number;
}

/** Previous session (UTC) high/low — Pine request.security(..., "D", high[1]) */
const prevSessionLevels = (candles: CandleData[]): { high: number; low: number } | null => {
  const days = new Map<string, { high: number; low: number }>();
  for (const c of candles) {
    const d = new Date(c.time * 1000).toISOString().slice(0, 10);
    const cur = days.get(d);
    if (cur) {
      if (c.high > cur.high) cur.high = c.high;
      if (c.low < cur.low) cur.low = c.low;
    } else {
      days.set(d, { high: c.high, low: c.low });
    }
  }
  const keys = [...days.keys()].sort();
  if (keys.length < 2) return null;
  return days.get(keys[keys.length - 2]) || null;
};

// ─── Indicator definition ────────────────────────────────────────────────────

export const orderBlocksIndicator: IndicatorDefinition = {
  id: "order_blocks",
  name: "Order Blocks",
  shortName: "OB",
  category: "advanced",
  pane: "overlay",
  params: OB_PARAMS,
  plots: [
    { key: "zones", label: "Order Blocks", color: "#C0E6AE", style: "line", lastValueVisible: false, priceLineVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const P: Record<string, any> = {};
    for (const pd of OB_PARAMS) P[pd.key] = params[pd.key] !== undefined ? params[pd.key] : pd.default;
    const {
      candleRange, bullBreakSource, bearBreakSource,
      showPD, showBearishBOS, showBullishBOS,
      showBreakerCandles, showRetestCandles, showTrendColours,
      useMitigatedBlocks, showArrows,
      bearishOBColor, bullishOBColor, mitigatedOBColor,
      breakerCandleColor, shortRetestColor, longRetestColor,
      trendBullColor, trendBearColor,
    } = P;

    const n = candles.length;
    const R = Math.max(1, Math.round(candleRange));
    const O = candles.map(c => c.open);
    const H = candles.map(c => c.high);
    const L = candles.map(c => c.low);
    const C = candles.map(c => c.close);
    const bullSrc = srcSeries(candles, String(bullBreakSource));
    const bearSrc = srcSeries(candles, String(bearBreakSource));

    // Pine `var` state — initial values match Pine's 0 defaults
    let lastDownIndex = 0, lastDown = 0, lastLow = 0;
    let lastUpIndex = 0, lastUpLow = 0, lastHigh = 0;
    let lastLongIndex = 0;
    let colourMode = 0;                 // CandleColourMode: 1 = bullish, 0 = bearish
    const longBoxes: ObBox[] = [];
    const shortBoxes: ObBox[] = [];
    const bosLines: BosLine[] = [];
    const buyBars = new Set<number>();
    const sellBars = new Set<number>();
    const candleColors: { time: number; color: string }[] = [];

    let prevStructureLow: number | null = null;

    for (let i = 0; i < n; i++) {
      // ── structure ────────────────────────────────────────────────────────
      // ta.lowest(low, R)[1] / ta.highest(high, R)[1]: window excludes current bar
      let structureLow = NaN;
      let structureLowIndex = 0;
      if (i >= R) {
        structureLow = Infinity;
        for (let k = i - R; k <= i - 1; k++) if (L[k] < structureLow) structureLow = L[k];
        // structureLowIndexPointer: running min starting from the window's highest high
        let minValue = -Infinity;
        for (let k = i - R; k <= i - 1; k++) if (H[k] > minValue) minValue = H[k];
        structureLowIndex = i;
        for (let p = 1; p <= R; p++) {
          if (L[i - p] < minValue) { minValue = L[i - p]; structureLowIndex = i - p; }
        }
      }

      // Per-bar event flags (Pine resets them at the top of each bar)
      let bosCandle = false;
      let bullishAlert = false;
      let bearishAlert = false;
      let shortRetest = false;
      let longRetest = false;

      // ── bearish break of structure ───────────────────────────────────────
      if (
        i >= R && isFinite(structureLow) && prevStructureLow !== null &&
        bearSrc[i] < structureLow && bearSrc[i - 1] >= prevStructureLow
      ) {
        if (i - lastUpIndex < 1000) {
          shortBoxes.push({ left: lastUpIndex, top: lastHigh, bottom: lastUpLow, start: i, state: 0 });
          if (showBearishBOS) {
            bosLines.push({ left: structureLowIndex, right: i, price: structureLow, color: BOS_BEAR, width: 2 });
            if (bosLines.length > MAX_BOS_LINES) bosLines.shift();
          }
          bosCandle = true;
          colourMode = 0;
          bearishAlert = true;
        }
      }

      // ── short boxes: mitigation + bullish break ──────────────────────────
      if (shortBoxes.length > 0) {
        for (let idx = shortBoxes.length - 1; idx >= 0; idx--) {
          const b = shortBoxes[idx];
          if (
            H[i] > b.bottom && L[i] < b.bottom &&
            i > b.start && b.state === 0 && useMitigatedBlocks
          ) {
            b.state = 1;
            shortRetest = true;
          }
          if (bullSrc[i] > b.top) {
            shortBoxes.splice(idx, 1);
            bullishAlert = true;
            // ok to draw? (prevents duplicate bullish OBs)
            if (i - lastDownIndex < 1000 && i > lastLongIndex) {
              longBoxes.push({ left: lastDownIndex, top: lastDown, bottom: lastLow, start: i + 1, state: 0 });
              if (showBullishBOS) {
                bosLines.push({ left: b.left, right: i, price: b.top, color: BOS_BULL, width: 1 });
                if (bosLines.length > MAX_BOS_LINES) bosLines.shift();
              }
              bosCandle = true;
              colourMode = 1;
              lastLongIndex = i;
            }
          }
        }
      }

      // ── long boxes: re-test + invalidation ───────────────────────────────
      if (longBoxes.length > 0) {
        for (let idx = longBoxes.length - 1; idx >= 0; idx--) {
          const b = longBoxes[idx];
          if (L[i] <= b.top && H[i] > b.top && i > b.start && b.state === 0) {
            b.state = 1;
            longRetest = true;
          }
          if (C[i] < b.bottom) {
            longBoxes.splice(idx, 1);
            sellBars.add(i);   // bullish order block broken by close
          }
        }
      }

      // ── buy/sell arrows (break events) ───────────────────────────────────
      if (bullishAlert) buyBars.add(i);
      if (bearishAlert) sellBars.add(i);

      // ── candle colouring (Pine barcolor chain, exact override order) ─────
      let col = colourMode === 1 ? trendBullColor : trendBearColor;
      if (showBreakerCandles && bosCandle) col = breakerCandleColor;
      if (showRetestCandles && shortRetest) col = shortRetestColor;
      if (showRetestCandles && longRetest) col = longRetestColor;
      if (showTrendColours || (showBreakerCandles && bosCandle)) {
        candleColors.push({ time: candles[i].time, color: col });
      }

      // ── record last up/down candles (after all uses, like Pine) ──────────
      if (C[i] < O[i]) {
        lastDown = H[i];
        lastDownIndex = i;
        lastLow = L[i];
      }
      if (C[i] > O[i]) {
        lastUpIndex = i;
        lastUpLow = L[i];
        lastHigh = H[i];
      }
      // running extremes for more accurate order-block placements
      if (H[i] > lastHigh) lastHigh = H[i];
      if (L[i] < lastLow) lastLow = L[i];

      prevStructureLow = i >= R && isFinite(structureLow) ? structureLow : null;
    }

    // ── band output: only boxes alive at the end (Pine box.delete removes
    //    invalidated boxes from the chart entirely) ─────────────────────────
    const bands: IndicatorBand[] = [];
    longBoxes.forEach((b, k) => {
      if (b.left < 0 || b.left >= n) return;
      bands.push({
        id: `ob_l_${k}_${b.left}`,
        top: b.top,
        bottom: b.bottom,
        fill: b.state === 1 && useMitigatedBlocks
          ? rgba(mitigatedOBColor, ALPHA_MITIGATED)
          : rgba(bullishOBColor, ALPHA_BULL),
        border: BOX_BORDER,
        fromTime: candles[b.left].time,
        lineWidth: 1,
      });
    });
    shortBoxes.forEach((b, k) => {
      if (b.left < 0 || b.left >= n) return;
      bands.push({
        id: `ob_s_${k}_${b.left}`,
        top: b.top,
        bottom: b.bottom,
        fill: b.state === 1
          ? rgba(mitigatedOBColor, ALPHA_MITIGATED)
          : rgba(bearishOBColor, ALPHA_BEAR),
        border: BOX_BORDER,
        fromTime: candles[b.left].time,
        lineWidth: 1,
      });
    });
    bosLines.forEach((l, k) => {
      if (l.left < 0 || l.right >= n || l.right < 0) return;
      bands.push({
        id: `ob_bos_${k}`,
        top: l.price,
        bottom: l.price,
        fill: l.color,
        border: l.color,
        fromTime: candles[l.left].time,
        toTime: candles[l.right].time,
        line: true,
        lineWidth: l.width,
      });
    });

    // ── buy/sell arrows ────────────────────────────────────────────────────
    const markers: IndicatorMarker[] = [];
    if (showArrows) {
      for (const i of buyBars) {
        markers.push({ time: candles[i].time, position: "belowBar", color: ARROW_UP_COLOR, shape: "arrowUp", text: "", size: 2 });
      }
      for (const i of sellBars) {
        markers.push({ time: candles[i].time, position: "aboveBar", color: ARROW_DOWN_COLOR, shape: "arrowDown", text: "", size: 2 });
      }
      markers.sort((a, b) => a.time - b.time);
    }

    // ── previous day high/low lines ────────────────────────────────────────
    const priceLines: IndicatorPriceLine[] = [];
    if (showPD) {
      const pd = prevSessionLevels(candles);
      if (pd) {
        priceLines.push({ id: "pdh", price: pd.high, color: PD_LINE, title: "PDH", lineStyle: 0, lineWidth: 1 });
        priceLines.push({ id: "pdl", price: pd.low, color: PD_LINE, title: "PDL", lineStyle: 0, lineWidth: 1 });
      }
    }

    return [
      {
        key: "zones",
        data: [],
        bands,
        markers,
        priceLines,
        metadata: { candleColors },
      },
    ];
  },
};
